"use client";

import { HighlightedTokens } from "src/components/common/Code/StaticCode";
import { renderTokens } from "src/components/common/Code/TokenRendering";
import { registerDocsHighlighting } from "src/lib/DocsHighlighting";
import {
  type DocsLanguage,
  getDocsLanguageSyntax,
  resolveDocsLanguage,
} from "src/lib/DocsLanguage";
import { useDocsLanguage } from "./DocsLanguageContext";
import DocsLanguageTabs from "./DocsLanguageTabs";
import styles from "./InlineSdkCode.module.scss";

export interface InlineSdkCallProps {
  /** The call in each language it has. */
  snippets: Partial<Record<DocsLanguage, string>>;
  variant: "inline" | "block";
  /** Build-time tokens for the snippets, from `encodeDocsHighlighting`. */
  highlighting?: string;
}

/** The client part of `InlineSdkCode`: shows the call in the reader's docs language. */
const InlineSdkCall = (props: InlineSdkCallProps) => {
  const { snippets, variant, highlighting } = props;
  registerDocsHighlighting(highlighting);
  const { language } = useDocsLanguage();
  const selected = resolveDocsLanguage(language, Object.keys(snippets) as DocsLanguage[], "inline");
  const value = snippets[selected] ?? "";
  const syntax = getDocsLanguageSyntax(selected);
  if (variant === "block")
    return (
      <DocsLanguageTabs
        tabs={Object.keys(snippets) as DocsLanguage[]}
        code={Object.fromEntries(
          Object.entries(snippets).map(([key, value]) => [
            key,
            [{ value, language: getDocsLanguageSyntax(key as DocsLanguage) }],
          ]),
        )}
        fallback="inline"
        highlighting={highlighting}
      />
    );
  return (
    <code className={styles.code} data-inline-sdk={selected}>
      <HighlightedTokens value={value} language={syntax} variant="inline" fallback={value}>
        {(tokens) => tokens.flatMap((line) => renderTokens(line))}
      </HighlightedTokens>
    </code>
  );
};

export default InlineSdkCall;
